import React, { useState, useEffect } from 'react';
import { ensureSessionHeaders } from '../../lib/serverSession';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

interface SystemSettingsManagerProps {
  currentUser: any;
}

export const SystemSettingsManager: React.FC<SystemSettingsManagerProps> = () => {
  const [withdrawalsEnabled, setWithdrawalsEnabled] = useState(true);
  const [chatbotEnabled, setChatbotEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [togglingChatbot, setTogglingChatbot] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [chatbotLastUpdated, setChatbotLastUpdated] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const headers = await ensureSessionHeaders();
      const res = await fetch(`${API_BASE_URL}/api/admin/system-settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ operation: 'get_settings' })
      });
      const result = await res.json();

      if (result.success && result.settings) {
        const withdrawalSetting = result.settings.find((s: any) => s.setting_name === 'withdrawals_enabled');
        if (withdrawalSetting) {
          setWithdrawalsEnabled(withdrawalSetting.setting_value === 'true');
          setLastUpdated(withdrawalSetting.updated_at);
        }
        const chatbotSetting = result.settings.find((s: any) => s.setting_name === 'chatbot_enabled');
        if (chatbotSetting) {
          setChatbotEnabled(chatbotSetting.setting_value === 'true');
          setChatbotLastUpdated(chatbotSetting.updated_at);
        }
      }
    } catch (err) {
      console.error('Error loading system settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleWithdrawals = async () => {
    const newValue = !withdrawalsEnabled;
    const confirmMsg = newValue
      ? 'Are you sure you want to ENABLE withdrawals? Users will be able to submit new withdrawal requests.'
      : 'Are you sure you want to DISABLE withdrawals? Users will NOT be able to submit new withdrawal requests. Existing pending withdrawals can still be processed.';

    if (!confirm(confirmMsg)) return;

    try {
      setToggling(true);
      const headers = await ensureSessionHeaders();
      const res = await fetch(`${API_BASE_URL}/api/admin/system-settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ operation: 'toggle_withdrawals', data: { enabled: newValue } })
      });
      const result = await res.json();

      if (result.success) {
        setWithdrawalsEnabled(newValue);
        setLastUpdated(new Date().toISOString());
        setStatusMessage(`✅ Withdrawals ${newValue ? 'enabled' : 'disabled'} successfully.`);
        setTimeout(() => setStatusMessage(null), 4000);
      } else {
        setStatusMessage(`❌ Failed: ${result.error}`);
      }
    } catch (err) {
      console.error('Error toggling withdrawals:', err);
      setStatusMessage('❌ Network error. Please try again.');
    } finally {
      setToggling(false);
    }
  };

  const handleToggleChatbot = async () => {
    const newValue = !chatbotEnabled;
    const confirmMsg = newValue
      ? 'Enable the AI Chatbot for ALL users? The chatbot will become visible to all authenticated shareholders and affiliates.'
      : 'Disable the AI Chatbot for public users? Only admins will be able to see and test the chatbot.';

    if (!confirm(confirmMsg)) return;

    try {
      setTogglingChatbot(true);
      const headers = await ensureSessionHeaders();
      const res = await fetch(`${API_BASE_URL}/api/admin/system-settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ operation: 'toggle_chatbot', data: { enabled: newValue } })
      });
      const result = await res.json();

      if (result.success) {
        setChatbotEnabled(newValue);
        setChatbotLastUpdated(new Date().toISOString());
        setStatusMessage(`✅ AI Chatbot ${newValue ? 'enabled for all users' : 'restricted to admins only'}.`);
        setTimeout(() => setStatusMessage(null), 4000);
      } else {
        setStatusMessage(`❌ Failed: ${result.error}`);
      }
    } catch (err) {
      console.error('Error toggling chatbot:', err);
      setStatusMessage('❌ Network error. Please try again.');
    } finally {
      setTogglingChatbot(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 text-center text-gray-400">
        <div className="animate-spin inline-block w-8 h-8 border-2 border-yellow-500 border-t-transparent rounded-full mb-2"></div>
        <p>Loading system settings...</p>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-6">
      <div>
        <h2 className="text-xl sm:text-2xl font-bold text-white mb-1">⚙️ System Settings</h2>
        <p className="text-gray-400 text-sm">Manage system-wide settings that affect all users.</p>
      </div>

      {statusMessage && (
        <div className={`p-3 rounded-lg text-sm font-medium ${statusMessage.startsWith('✅') ? 'bg-green-900/30 border border-green-500/40 text-green-300' : 'bg-red-900/30 border border-red-500/40 text-red-300'}`}>
          {statusMessage}
        </div>
      )}

      {/* Withdrawal Toggle */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-4 sm:p-6">
        <div className="flex items-center justify-between">
          <div className="flex-1 mr-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              💸 Withdrawals
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${withdrawalsEnabled ? 'bg-green-900/50 text-green-300 border border-green-500/30' : 'bg-red-900/50 text-red-300 border border-red-500/30'}`}>
                {withdrawalsEnabled ? 'ENABLED' : 'DISABLED'}
              </span>
            </h3>
            <p className="text-gray-400 text-sm mt-1">
              {withdrawalsEnabled
                ? 'Users can submit new withdrawal requests.'
                : 'New withdrawal requests are blocked. Admins can still process existing pending withdrawals.'}
            </p>
            {lastUpdated && (
              <p className="text-gray-500 text-xs mt-2">
                Last updated: {new Date(lastUpdated).toLocaleString()}
              </p>
            )}
          </div>
          <button
            onClick={handleToggleWithdrawals}
            disabled={toggling}
            className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 focus:ring-offset-gray-900 ${
              withdrawalsEnabled ? 'bg-green-600' : 'bg-gray-600'
            } ${toggling ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            <span className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform shadow-md ${
              withdrawalsEnabled ? 'translate-x-7' : 'translate-x-1'
            }`} />
          </button>
        </div>
      </div>

      {/* AI Chatbot Toggle */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-4 sm:p-6">
        <div className="flex items-center justify-between">
          <div className="flex-1 mr-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              🤖 AI Chatbot
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${chatbotEnabled ? 'bg-green-900/50 text-green-300 border border-green-500/30' : 'bg-orange-900/50 text-orange-300 border border-orange-500/30'}`}>
                {chatbotEnabled ? 'PUBLIC' : 'ADMIN ONLY'}
              </span>
            </h3>
            <p className="text-gray-400 text-sm mt-1">
              {chatbotEnabled
                ? 'The AI chatbot is visible to all authenticated users (shareholders & affiliates).'
                : 'The AI chatbot is only visible to admins for testing. Regular users cannot see it.'}
            </p>
            {chatbotLastUpdated && (
              <p className="text-gray-500 text-xs mt-2">
                Last updated: {new Date(chatbotLastUpdated).toLocaleString()}
              </p>
            )}
          </div>
          <button
            onClick={handleToggleChatbot}
            disabled={togglingChatbot}
            className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 focus:ring-offset-gray-900 ${
              chatbotEnabled ? 'bg-green-600' : 'bg-gray-600'
            } ${togglingChatbot ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            <span className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform shadow-md ${
              chatbotEnabled ? 'translate-x-7' : 'translate-x-1'
            }`} />
          </button>
        </div>
      </div>

      {/* Info Notices */}
      <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4 text-sm text-blue-300">
        <strong>ℹ️ Note:</strong> Disabling withdrawals only prevents <em>new</em> requests.
        Admins can still approve or reject existing pending withdrawals from the Withdrawal Management page.
      </div>
      <div className="bg-amber-900/20 border border-amber-500/30 rounded-lg p-4 text-sm text-amber-300">
        <strong>🤖 Chatbot Note:</strong> When the AI chatbot is set to <em>Admin Only</em>,
        admins can fully test the Gemini AI integration, voice input, and escalation workflows without
        exposing the feature to live shareholders. Toggle to <em>Public</em> when ready for launch.
      </div>
    </div>
  );
};

